// ABOUTME: Thread card for work Marketing handed to a team member (a production brief). It
// updates live from the event stream: queued (its place in the role's queue and the brief it
// waits behind), the role working (its current step), waiting for the owner's answer to the
// member's post, then the result with previews of what was made and the link to it, or the
// failure with its reason. The status words and the link come from the brief's view. The card
// is the one place a producer's progress shows; Marketing's own steps are in its run's
// activity row.
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Check, CircleAlert, Clock, Loader2 } from 'lucide-react'
import {
  getProductionBrief,
  PRODUCTION_BRIEFS_KEY,
  type ProductionBrief,
} from '@/api/productionBriefs'
import { CHAT_CARD_BLOCK } from './chatCard'

function StateIcon({ state }: { state: ProductionBrief['state'] }) {
  if (state === 'completed') return <Check className="text-success size-4" aria-hidden />
  if (state === 'failed') return <CircleAlert className="text-destructive size-4" aria-hidden />
  if (state === 'queued' || state === 'waiting')
    return <Clock className="text-muted-foreground size-4" aria-hidden />
  return <Loader2 className="text-muted-foreground size-4 animate-spin" aria-hidden />
}

function Previews({ brief }: { brief: ProductionBrief }) {
  if (brief.previews.length === 0) return null
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="brief-previews">
      {brief.previews.map((preview) => {
        const src = preview.thumbnail_url ?? preview.url
        return (
          <a
            key={preview.asset_id}
            href={preview.url ?? undefined}
            target="_blank"
            rel="noreferrer"
            className="bg-muted block aspect-square overflow-hidden rounded-md"
            data-testid="brief-preview"
          >
            {src && preview.kind !== 'video' ? (
              <img src={src} alt="" className="size-full object-cover" loading="lazy" />
            ) : src ? (
              <video src={src} className="size-full object-cover" muted playsInline />
            ) : null}
          </a>
        )
      })}
    </div>
  )
}

export function ProductionBriefCard({ briefId }: { briefId: string }) {
  const { data: brief } = useQuery({
    queryKey: [PRODUCTION_BRIEFS_KEY, briefId],
    queryFn: () => getProductionBrief(briefId),
  })

  return (
    <div
      id={`brief-${briefId}`}
      className={CHAT_CARD_BLOCK}
      data-testid="production-brief-card"
      data-brief-id={briefId}
      data-state={brief?.state}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-muted-foreground truncate text-xs font-medium">
          {brief?.role_name ? `Handed to ${brief.role_name}` : 'Hand-off'}
        </span>
        {brief ? (
          <span
            className="flex shrink-0 items-center gap-1.5 text-xs font-medium"
            data-testid="brief-status"
          >
            <StateIcon state={brief.state} />
            {brief.status_line}
          </span>
        ) : null}
      </div>
      <p className="text-sm font-medium break-words">{brief?.title ?? 'Loading…'}</p>
      {brief?.state === 'working' && brief.current_step ? (
        <p className="text-muted-foreground text-xs" data-testid="brief-step">
          {brief.current_step}
        </p>
      ) : null}
      {brief?.state === 'failed' && brief.last_error ? (
        <p className="text-muted-foreground text-sm">{brief.last_error}</p>
      ) : null}
      {brief?.state === 'completed' ? <Previews brief={brief} /> : null}
      {brief?.link ? (
        <Link
          to={brief.link.path}
          className="text-primary inline-flex min-h-11 items-center text-sm underline underline-offset-2"
          data-testid="brief-link"
        >
          {brief.link.label}
        </Link>
      ) : null}
    </div>
  )
}
